import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';
import { InventoryAdjustmentDto, InventoryAdjustmentStatus, AdjustmentType } from '../../models/inventory/inventory-adjustment';
import { InventoryAdjustmentProxyService } from '../../proxies/inventory/inventory-adjustment.proxy.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-inventory-adjustment',
  templateUrl: './inventory-adjustment.component.html',
  styleUrl: './inventory-adjustment.component.scss',
})
export class InventoryAdjustmentComponent implements OnInit {
  isLoading = signal<boolean>(false);
  adjustments: InventoryAdjustmentDto[] = [];
  totalRecords = 0;
  take = 10;

  items = [{ label: 'Inventory', route: '/' }, { label: 'Inventory Adjustments' }];

  menuItems: MenuItem[] = [
    {
      label: 'Standard Adjustment',
      icon: 'pi pi-file-edit',
      command: () => this.createNew(),
    },
    {
      label: 'Lot-Based Reduction',
      icon: 'pi pi-box',
      command: () => this.createLotReduction(),
    },
    {
      label: 'Create Lot Item',
      icon: 'pi pi-plus-circle',
      command: () => this.createLotItem(),
    },
  ];

  typeLabels = {
    [AdjustmentType.LocationTransfer]: 'Location Transfer',
    [AdjustmentType.QuantityCorrection]: 'Quantity Correction',
    [AdjustmentType.LocationAndQuantity]: 'Location & Quantity',
  };

  private readonly translateService = inject(TranslateService);

  constructor(
    private inventoryAdjustmentProxyService: InventoryAdjustmentProxyService,
    private confirmationService: ConfirmationService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadAdjustments();
  }

  loadAdjustments(): void {
    this.isLoading.set(true);

    this.inventoryAdjustmentProxyService
      .getAll()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.adjustments = response.data;
            this.totalRecords = response.data.length;
          } else {
            this.adjustments = [];
            this.totalRecords = 0;
          }
        },
        error: () => {
          this.adjustments = [];
          this.totalRecords = 0;
        },
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  onPageChange(event: any): void {
    // Pagination is handled client-side now since we load all data
  }

  createNew(): void {
    this.router.navigate(['/inventory-adjustment-details'], { queryParams: { id: 'new' } });
  }

  createLotReduction(): void {
    this.router.navigate(['/inventory-adjustment-details'], { queryParams: { id: 'new', type: 'lot-reduction' } });
  }

  createLotItem(): void {
    this.router.navigate(['/inventory-adjustment-details'], { queryParams: { id: 'new', type: 'lot-item' } });
  }

  viewDetails(adjustment: InventoryAdjustmentDto): void {
    if (adjustment.id) {
      this.router.navigate(['/inventory-adjustment-details'], { queryParams: { id: adjustment.id } });
    }
  }

  voidAdjustment(adjustment: InventoryAdjustmentDto): void {
    if (!adjustment.id) return;

    this.confirmationService.confirm({
      key: 'main',
      message: `Are you sure you want to void adjustment ${adjustment.number}? This action cannot be undone and will NOT reverse inventory changes.`,
      header: 'Confirm Void',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isLoading.set(true);
        this.inventoryAdjustmentProxyService
          .void(adjustment.id!)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: (response) => {
              if (response.success) {
                GlobalMessageUtil.success('Success', 'Adjustment voided successfully');
                this.loadAdjustments();
              } else {
                const errorMsg = getLocalizedValue(this.translateService, response.errorMessage) || 'Failed to void adjustment';
                GlobalMessageUtil.error('Error', errorMsg);
              }
            },
          });
      },
    });
  }

  getStatusSeverity(status: InventoryAdjustmentStatus): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' {
    switch (status) {
      case InventoryAdjustmentStatus.Completed:
        return 'success';
      case InventoryAdjustmentStatus.Voided:
        return 'danger';
      case InventoryAdjustmentStatus.Draft:
        return 'warn';
      default:
        return 'info';
    }
  }

  getTypeLabel(type: AdjustmentType): string {
    return this.typeLabels[type] || AdjustmentType[type];
  }

  getStatusLabel(status: InventoryAdjustmentStatus): string {
    return InventoryAdjustmentStatus[status];
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  }

  canVoid(adjustment: InventoryAdjustmentDto): boolean {
    return adjustment.status === InventoryAdjustmentStatus.Completed;
  }
}
